'use client';

import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { GoogleMap, MarkerF } from '@react-google-maps/api';
import { LocationStatsWorkspace } from 'hotdesk_typescript';
import { useGoogleMapsLoaded } from '@/hotdesk-ui/helpers/google-maps-loader';
import styles from './LocationPages.module.scss';

/** Dark base with white pins, as in the design. */
const DARK_STYLE: google.maps.MapTypeStyle[] = [{
  elementType: 'geometry',
  stylers: [{
    color: '#161616'
  }]
}, {
  elementType: 'labels.text.fill',
  stylers: [{
    color: '#8a8a8a'
  }]
}, {
  elementType: 'labels.text.stroke',
  stylers: [{
    color: '#161616'
  }]
}, {
  elementType: 'labels.icon',
  stylers: [{
    visibility: 'off'
  }]
}, {
  featureType: 'poi',
  stylers: [{
    visibility: 'off'
  }]
}, {
  featureType: 'transit',
  stylers: [{
    visibility: 'off'
  }]
}, {
  featureType: 'road',
  elementType: 'geometry',
  stylers: [{
    color: '#2c2c2c'
  }]
}, {
  featureType: 'road',
  elementType: 'labels',
  stylers: [{
    visibility: 'off'
  }]
}, {
  featureType: 'water',
  elementType: 'geometry',
  stylers: [{
    color: '#0f2a33'
  }]
}, {
  featureType: 'administrative',
  elementType: 'geometry',
  stylers: [{
    visibility: 'off'
  }]
}];
const toLatLng = (workspace: LocationStatsWorkspace) => {
  const [lng, lat] = workspace.location.coordinates;
  return {
    lat,
    lng
  };
};
interface CityHeroMapProps {
  workspaces: LocationStatsWorkspace[];
  /** City centre ([lng, lat]) for a city whose workspaces have no location. */
  fallbackCenter?: [number, number];
  label: string;
}

/** The live map; only mounted once the hero scrolls into view. */
const LiveMap: FC<CityHeroMapProps> = ({
  workspaces,
  fallbackCenter
}) => {
  const loaded = useGoogleMapsLoaded();
  const fitPins = useCallback((map: google.maps.Map) => {
    if (workspaces.length === 0) return;
    if (workspaces.length === 1) {
      map.setCenter(toLatLng(workspaces[0]));
      map.setZoom(14);
      return;
    }
    const bounds = new google.maps.LatLngBounds();
    workspaces.forEach(workspace => bounds.extend(toLatLng(workspace)));
    map.fitBounds(bounds, 48);
  }, [workspaces]);
  if (!loaded) return null;
  const pin: google.maps.Symbol = {
    path: google.maps.SymbolPath.CIRCLE,
    scale: 5,
    fillColor: '#fff',
    fillOpacity: 1,
    strokeColor: '#161616',
    strokeWeight: 1.5
  };
  const center = fallbackCenter ? {
    lat: fallbackCenter[1],
    lng: fallbackCenter[0]
  } : workspaces[0] ? toLatLng(workspaces[0]) : {
    lat: 0,
    lng: 0
  };
  return <GoogleMap mapContainerClassName={styles.mapCanvas} center={center} zoom={12} onLoad={fitPins} options={{
    styles: DARK_STYLE,
    backgroundColor: '#161616',
    disableDefaultUI: true,
    zoomControl: true,
    gestureHandling: 'cooperative',
    clickableIcons: false,
    keyboardShortcuts: false
  }} data-sentry-element="GoogleMap" data-sentry-component="LiveMap" data-sentry-source-file="CityHeroMap.tsx">
            {workspaces.map(workspace => <MarkerF key={workspace.id} position={toLatLng(workspace)} icon={pin} title={workspace.title} />)}
        </GoogleMap>;
};

/**
 * Hero map of every workspace in the city. The Maps script (~250 KB) loads
 * only when the map is about to be seen, so first paint stays fast; until then
 * the dark placeholder holds its place (role="img" + label for screen readers).
 */
const CityHeroMap: FC<CityHeroMapProps> = props => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node || visible) return;
    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) {
        setVisible(true);
        observer.disconnect();
      }
    }, {
      rootMargin: '200px'
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [visible]);
  return <div ref={ref} className={styles.mapCanvas} role="img" aria-label={props.label} data-sentry-component="CityHeroMap" data-sentry-source-file="CityHeroMap.tsx">
            {visible && <LiveMap {...props} />}
        </div>;
};
export default CityHeroMap;